import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, X, Plus, Calendar as CalendarIcon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { expensesApi, expenseCategoriesApi } from "@/lib/api";

const MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];

// Convert user "12.34" → cents. Never trust floats.
function parseCents(str) {
  const s = String(str || "").replace(/[^0-9.]/g, "");
  if (!s) return 0;
  const [whole, frac = ""] = s.split(".");
  const cents = parseInt(whole || "0", 10) * 100 + parseInt((frac + "00").slice(0, 2), 10);
  return Number.isNaN(cents) ? 0 : cents;
}
function fmtCents(cents) {
  if (!cents) return "";
  const c = Math.abs(cents);
  return `${Math.floor(c / 100)}.${String(c % 100).padStart(2, "0")}`;
}

export function ExpenseDialog({
  open,
  onOpenChange,
  projectId,
  expense,
  categories,
  onSaved,
  onCategoriesChanged,
}) {
  const isEdit = !!expense;
  const [expenseDate, setExpenseDate] = useState(() =>
    (expense?.expense_date || new Date().toISOString().slice(0, 10))
  );
  const [amount, setAmount] = useState(() => fmtCents(expense?.amount_cents || 0));
  const [merchant, setMerchant] = useState(expense?.merchant || "");
  const [categoryId, setCategoryId] = useState(expense?.category_id || "");
  const [reimbursable, setReimbursable] = useState(!!expense?.reimbursable);
  const [notes, setNotes] = useState(expense?.notes || "");
  const [receipt, setReceipt] = useState(null);
  const [clearReceipt, setClearReceipt] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addingNew, setAddingNew] = useState(false);
  const [newCatName, setNewCatName] = useState("");

  const currentCategoryName =
    expense?.category_name ||
    (categories.find((c) => c.id === expense?.category_id) || {}).name ||
    "";

  useEffect(() => {
    if (!isEdit && !categoryId && categories.length > 0) {
      // Default new expenses to the first category so the form is submittable.
      setCategoryId(categories[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories.length]);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ALLOWED_TYPES.includes(f.type)) {
      toast.error("Receipt must be JPG, PNG, or PDF");
      e.target.value = "";
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error("Receipt is over 10MB");
      e.target.value = "";
      return;
    }
    setReceipt(f);
    setClearReceipt(false);
  };

  const handleAddCategoryInline = async () => {
    const name = newCatName.trim();
    if (!name) return;
    try {
      const created = await expenseCategoriesApi.create(name);
      toast.success(`Added "${created.name}"`);
      setCategoryId(created.id);
      setNewCatName("");
      setAddingNew(false);
      await onCategoriesChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add category");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!merchant.trim()) {
      toast.error("Merchant is required");
      return;
    }
    const cents = parseCents(amount);
    if (cents <= 0) {
      toast.error("Amount must be greater than $0");
      return;
    }
    setSaving(true);
    try {
      const form = new FormData();
      form.append("expense_date", expenseDate);
      form.append("amount_cents", String(cents));
      form.append("merchant", merchant.trim());
      form.append("reimbursable", String(reimbursable));
      form.append("notes", notes.trim());
      if (categoryId) form.append("category_id", categoryId);
      if (receipt) form.append("receipt", receipt);
      if (isEdit && clearReceipt) form.append("clear_receipt", "true");
      if (isEdit) {
        await expensesApi.update(projectId, expense.id, form);
        toast.success("Expense updated");
      } else {
        await expensesApi.create(projectId, form);
        toast.success("Expense logged");
      }
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save expense");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="expense-dialog"
        className="bg-zinc-950 border-zinc-800 text-zinc-100 sm:max-w-lg max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light">
            {isEdit ? "Edit expense" : "Log expense"}
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            All amounts in USD. Receipts attach to the expense and can be viewed later.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="exp-date" className="text-overline text-zinc-500">Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    id="exp-date"
                    type="button"
                    data-testid="expense-date-input"
                    className="w-full mt-1 h-9 flex items-center gap-2 px-3 py-2 rounded-md text-sm bg-zinc-950 border border-zinc-800 text-zinc-100 hover:bg-zinc-900 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    <CalendarIcon className="w-4 h-4 text-zinc-500" />
                    <span>
                      {expenseDate
                        ? new Date(expenseDate + "T00:00:00").toLocaleDateString(undefined, {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })
                        : "Pick a date"}
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-auto p-0 bg-zinc-950 border-zinc-800"
                  data-testid="expense-date-popover"
                  align="start"
                >
                  <Calendar
                    mode="single"
                    selected={expenseDate ? new Date(expenseDate + "T00:00:00") : undefined}
                    onSelect={(d) => {
                      if (!d) return;
                      const y = d.getFullYear();
                      const m = String(d.getMonth() + 1).padStart(2, "0");
                      const day = String(d.getDate()).padStart(2, "0");
                      setExpenseDate(`${y}-${m}-${day}`);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label htmlFor="exp-amount" className="text-overline text-zinc-500">Amount (USD)</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 font-mono">$</span>
                <Input
                  id="exp-amount"
                  data-testid="expense-amount-input"
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  required
                  className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 pl-7 font-mono"
                />
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="exp-merchant" className="text-overline text-zinc-500">Merchant</Label>
            <Input
              id="exp-merchant"
              data-testid="expense-merchant-input"
              value={merchant}
              onChange={(e) => setMerchant(e.target.value)}
              placeholder="Where you spent it"
              required
              className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 mt-1"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-overline text-zinc-500">Category</Label>
              {!addingNew && (
                <button
                  type="button"
                  data-testid="add-category-inline-btn"
                  onClick={() => setAddingNew(true)}
                  className="text-xs text-amber-400 hover:text-amber-300 inline-flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" /> Add new
                </button>
              )}
            </div>
            {addingNew ? (
              <div className="flex items-center gap-2">
                <Input
                  data-testid="new-category-name-input"
                  value={newCatName}
                  autoFocus
                  onChange={(e) => setNewCatName(e.target.value)}
                  placeholder="New category name"
                  className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 flex-1"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddCategoryInline();
                    }
                  }}
                />
                <Button
                  type="button"
                  data-testid="new-category-save-btn"
                  onClick={handleAddCategoryInline}
                  disabled={!newCatName.trim()}
                  className="bg-amber-500 text-black hover:bg-amber-400"
                >
                  Add
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setAddingNew(false);
                    setNewCatName("");
                  }}
                  className="text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900"
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <select
                data-testid="expense-category-select"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-amber-500"
              >
                {categories.length === 0 && <option value="">— none —</option>}
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
                {isEdit && currentCategoryName && !categories.find((c) => c.id === categoryId) && (
                  <option value={categoryId}>{currentCategoryName} (archived)</option>
                )}
              </select>
            )}
          </div>

          <div className="flex items-center justify-between p-3 rounded-sm bg-zinc-900/40 border border-zinc-800">
            <div>
              <div className="text-sm text-zinc-200">Reimbursable</div>
              <div className="text-xs text-zinc-500">
                Flag this expense to be paid back to whoever fronted it.
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                data-testid="expense-reimbursable-toggle"
                checked={reimbursable}
                onChange={(e) => setReimbursable(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-10 h-5 bg-zinc-800 peer-checked:bg-emerald-500 rounded-full transition-colors">
                <div
                  className={`w-4 h-4 rounded-full bg-white shadow-md transform transition-transform mt-0.5 ${
                    reimbursable ? "translate-x-5" : "translate-x-0.5"
                  }`}
                />
              </div>
            </label>
          </div>

          <div>
            <Label htmlFor="exp-notes" className="text-overline text-zinc-500">Notes (optional)</Label>
            <Textarea
              id="exp-notes"
              data-testid="expense-notes-input"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything worth remembering later"
              className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 mt-1"
            />
          </div>

          <div>
            <Label className="text-overline text-zinc-500">Receipt (JPG, PNG or PDF · max 10MB)</Label>
            {isEdit && expense.receipt_filename && !receipt && !clearReceipt ? (
              <div className="mt-1 flex items-center justify-between p-2 rounded-sm bg-zinc-900/40 border border-zinc-800">
                <span className="text-xs text-zinc-400 truncate">
                  Current: {expense.receipt_filename}
                </span>
                <button
                  type="button"
                  data-testid="clear-receipt-btn"
                  onClick={() => setClearReceipt(true)}
                  className="text-xs text-rose-400 hover:text-rose-300 inline-flex items-center gap-1"
                >
                  <X className="w-3 h-3" /> Remove
                </button>
              </div>
            ) : (
              <label
                htmlFor="exp-receipt"
                data-testid="expense-receipt-input-label"
                className="mt-1 flex items-center justify-center gap-2 py-3 border border-dashed border-zinc-800 rounded-sm cursor-pointer hover:border-amber-500/60 hover:bg-zinc-900/40 transition-colors text-sm text-zinc-400"
              >
                <Upload className="w-4 h-4" />
                {receipt ? receipt.name : "Click to attach receipt"}
                <input
                  id="exp-receipt"
                  data-testid="expense-receipt-input"
                  type="file"
                  accept="image/jpeg,image/png,application/pdf"
                  onChange={handleFile}
                  className="hidden"
                />
              </label>
            )}
          </div>

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
              className="bg-transparent border-zinc-800 text-zinc-300 hover:bg-zinc-900"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="save-expense-btn"
              disabled={saving}
              className="bg-amber-500 text-black hover:bg-amber-400"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…
                </>
              ) : isEdit ? (
                "Save changes"
              ) : (
                "Log expense"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
